/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';
import { useMediaQuery } from 'hooks/useMediaQuery';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './HowItWorksSection.module.scss';

type Translate = (key: string) => string;

const STICK_TOP = 24;

interface DrowProps {
  label: string;
  value: string;
  variant?: 'mono' | 'blue' | 'muted' | 'key';
}

const Drow = ({ label, value, variant }: DrowProps) => (
  <div className={styles.drow}>
    <div className={styles.drowKey}>{label}</div>
    <div className={cn(styles.drowValue, variant && styles[variant])}>{value}</div>
  </div>
);

interface PanelProps {
  head: string;
  note: React.ReactNode;
  children: React.ReactNode;
}

const Panel = ({ head, note, children }: PanelProps) => (
  <div className={styles.panel}>
    <div className={styles.panelHead}>{head}</div>
    {children}
    <div className={styles.note}>{note}</div>
  </div>
);

interface FormatRow {
  country: string;
  input: string;
  note: string;
  ok: boolean;
}

const FORMAT_ROWS: FormatRow[] = [
  { country: 'Germany', input: 'DE811234567', note: 'DE + 9 digits', ok: true },
  { country: 'Netherlands', input: 'NL812345678B01', note: 'NL + 9 digits + B + 2', ok: true },
  { country: 'Italy', input: 'IT0123456789', note: 'expected 11 digits, got 10', ok: false },
  { country: 'United Kingdom', input: 'GB 123456789', note: 'space normalised', ok: true },
  { country: 'Germany', input: 'DEDE811234567', note: 'country code pasted twice', ok: false },
  { country: 'India', input: '22AAAAA0000A1Z5', note: 'state code + PAN + entity', ok: true },
  { country: 'United States', input: '47-1234567', note: 'EIN, 9 digits', ok: true },
  { country: 'Brazil', input: '12345678000199', note: 'check digits do not match', ok: false },
];

const FormatCheckPanel = () => {
  const { t } = useInt();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(wrapRef, { continuous: true, threshold: 0.3 });

  const [rowState, setRowState] = useState<('idle' | 'busy' | 'done')[]>(FORMAT_ROWS.map(() => 'idle'));
  const startedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!inView || startedRef.current) {
      return undefined;
    }

    startedRef.current = true;

    const run = () => {
      setRowState(FORMAT_ROWS.map(() => 'idle'));

      FORMAT_ROWS.forEach((_, i) => {
        const t0 = 220 + i * 520;

        timersRef.current.push(
          setTimeout(() => {
            setRowState((prev) => prev.map((s, n) => (n === i ? 'busy' : s)));
          }, t0),
        );

        timersRef.current.push(
          setTimeout(() => {
            setRowState((prev) => prev.map((s, n) => (n === i ? 'done' : s)));
          }, t0 + 620),
        );
      });

      timersRef.current.push(setTimeout(run, 220 + FORMAT_ROWS.length * 520 + 3400));
    };

    run();

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [inView]);

  return (
    <Panel
      head={t('Structure applied before query')}
      note={
        <>
          {t(
            'A typo, a trailing space, or a country code pasted twice. Catching those locally keeps authority throughput available for the numbers that matter.',
          )}
        </>
      }
    >
      <div className={styles.fchk} ref={wrapRef}>
        {FORMAT_ROWS.map((row, i) => (
          <div
            key={row.input}
            className={cn(
              styles.fcRow,
              rowState[i] !== 'idle' && styles.fcRowIn,
              rowState[i] === 'busy' && styles.fcRowBusy,
              rowState[i] === 'done' && styles.fcRowDone,
            )}
          >
            <span className={styles.fcCountry}>{row.country}</span>
            <span className={styles.fcInput}>{row.input}</span>
            <span className={styles.fcNote}>{row.note}</span>
            <span className={cn(styles.fcVerdict, row.ok ? styles.fcVerdictOk : styles.fcVerdictNo)}>
              {row.ok ? 'Pass' : 'Rejected'}
            </span>
            <span className={styles.fcBar}>
              <i />
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const AuthorityCheckPanel = () => {
  const { t } = useInt();

  return (
    <Panel
      head={t('Three outcomes, handled separately')}
      note={
        <>
          {t(
            'Treating an outage as a rejection is how compliant counterparties get blocked at checkout and how zero-rating gets applied on a number that was never actually confirmed. The distinction is carried through to the API response.',
          )}
        </>
      }
    >
      <div className={styles.state}>
        <span className={styles.stateOk}>Valid</span>
        <span className={styles.stateNo}>Invalid</span>
        <span className={styles.stateUn}>Unavailable</span>
      </div>

      <Drow label="Valid" value="Registered at the moment of the query. Response and reference stored." />
      <Drow label="Invalid" value="Not registered for cross-border trade in that jurisdiction." />
      <Drow
        label="Unavailable"
        value="The authority could not answer. Queued, retried on a backoff schedule, resolved without you polling."
      />
    </Panel>
  );
};

const RegistryPanel = () => {
  const { t } = useInt();

  return (
    <Panel
      head={t('Number resolved to entity')}
      note={
        <>
          {t(
            'This is the step nobody else takes. A tax authority tells you a number is registered, or that a name matches. The company register tells you which legal entity holds it, whether it is still trading, and where it sits in a group. The EIN path works the same way — match confirmed by the IRS, entity resolved from state filings.',
          )}
        </>
      }
    >
      <Drow label="Tax number" value="DE811234567" variant="mono" />
      <Drow label="Authority returned" value="Validity only" variant="muted" />
      <Drow label="Legal name" value="Acme Deutschland GmbH" variant="blue" />
      <Drow label="Registry" value="Handelsregister · Amtsgericht Charlottenburg" />
      <Drow label="Registration no." value="HRB 128374 B" variant="mono" />
      <Drow label="Status" value="Active" />
      <Drow label="Ultimate parent" value="Acme Holdings plc · United Kingdom" />
    </Panel>
  );
};

const EvidencePanel = () => {
  const { t } = useInt();

  return (
    <Panel
      head={t('Evidence retained per check')}
      note={
        <>
          {t(
            'Exportable per counterparty, per entity, or across a date range — so the answer to “prove you checked before you zero-rated that supply” is a query, not an archaeology project.',
          )}
        </>
      }
    >
      <Drow label="Record ID" value="chk_9f2b40c7e1" variant="mono" />
      <Drow label="Submitted" value="2026-07-27T09:14:02Z" variant="mono" />
      <Drow label="Requester number" value="GB260423730" variant="mono" />
      <Drow label="Consultation ref." value="WAPIAAAAXk91Rr" variant="key" />
      <Drow label="Authority" value="Bundeszentralamt für Steuern" />
      <Drow label="Result" value="Valid" />
      <Drow label="Registry snapshot" value="Stored at time of check" />
    </Panel>
  );
};

interface Step {
  number: string;
  title: string;
  description: string;
  media: React.ReactNode;
}

const getSteps = (t: Translate): Step[] => [
  {
    number: '01',
    title: t('Format and check digit'),
    description: t(
      'Country-specific structure and checksum applied before any network call, so malformed input never consumes an authority query.',
    ),
    media: <FormatCheckPanel />,
  },
  {
    number: '02',
    title: t('Authority check'),
    description: t(
      'Live query to the issuing tax administration. Three outcomes are handled distinctly, and unavailable results are queued and retried, not failed.',
    ),
    media: <AuthorityCheckPanel />,
  },
  {
    number: '03',
    title: t('Registry resolution'),
    description: t(
      'The number is matched to the legal entity in the national company register — including in jurisdictions where the tax authority returns nothing but a validity flag.',
    ),
    media: <RegistryPanel />,
  },
  {
    number: '04',
    title: t('Evidence record'),
    description: t(
      'An immutable record of the check: input, response, source, timestamp, consultation reference. Retrievable by counterparty or by date range.',
    ),
    media: <EvidencePanel />,
  },
];

export const HowItWorksSection = () => {
  const { t } = useInt();
  const isDesktop = useMediaQuery(1024);

  const steps = getSteps(t);

  const stageRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isDesktop) {
      return undefined;
    }

    let raf: number;

    const tick = () => {
      const stage = stageRef.current;
      const sticky = stickyRef.current;

      if (stage && sticky) {
        const rect = stage.getBoundingClientRect();
        const travel = Math.max(1, stage.offsetHeight - sticky.offsetHeight);
        const frac = Math.min(0.9999, Math.max(0, (STICK_TOP - rect.top) / travel));

        setStep(Math.floor(frac * steps.length));
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [isDesktop]);

  const scrollToStep = (index: number) => {
    const stage = stageRef.current;
    const sticky = stickyRef.current;

    if (!stage || !sticky) {
      return;
    }

    const top = stage.getBoundingClientRect().top + window.scrollY - STICK_TOP;
    const travel = Math.max(1, stage.offsetHeight - sticky.offsetHeight);

    window.scrollTo({ top: top + (travel / steps.length) * index + 12, behavior: 'smooth' });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            theme="dark"
            title={t('Four things happen on every check')}
            description={t('Format. Authority. Registry. Evidence. In that order, on every single number.')}
          />
        </div>

        <div className={styles.scrolly}>
          <div className={styles.rail}>
            {steps.map((item, i) => (
              <div
                key={item.number}
                className={cn(styles.railItem, (!isDesktop || step === i) && styles.railItemOn)}
                onClick={() => isDesktop && scrollToStep(i)}
              >
                <h3>
                  <em>{item.number}</em> {item.title}
                </h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>

          <div className={styles.stage} ref={stageRef} style={isDesktop ? { height: steps.length * 620 } : undefined}>
            <div className={styles.stageSticky} ref={stickyRef}>
              {steps.map((item, i) => (
                <div
                  key={item.number}
                  className={cn(styles.panelWrap, (!isDesktop || step === i) && styles.panelWrapOn)}
                >
                  {item.media}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
